import { useEffect, useState } from 'react';
import { toast } from '@scholastic/mockingjay';
import {
  GOOGLE_CONSENT_ACCOUNT_MISMATCH,
  GOOGLE_CONSENT_ACCOUNT_MISMATCH_MESSAGE,
  GOOGLE_CONSENT_ERROR_QUERY_PARAM,
  GOOGLE_IMPORT_SOURCE_QUERY_PARAM,
  type GoogleImportSource,
} from './use-get-google-courses-api';

interface GoogleImportReturnState {
  consentError: string | null;
  shouldAutoOpen: boolean;
}

function getGoogleImportReturnState(
  expectedSource: GoogleImportSource
): GoogleImportReturnState {
  const currentUrl = new URL(window.location.href);
  const importSource = currentUrl.searchParams.get(
    GOOGLE_IMPORT_SOURCE_QUERY_PARAM
  );

  if (importSource !== expectedSource) {
    return {
      consentError: null,
      shouldAutoOpen: false,
    };
  }

  return {
    consentError: currentUrl.searchParams.get(GOOGLE_CONSENT_ERROR_QUERY_PARAM),
    shouldAutoOpen: true,
  };
}

function clearGoogleImportReturnQueryParams() {
  const currentUrl = new URL(window.location.href);

  currentUrl.searchParams.delete(GOOGLE_IMPORT_SOURCE_QUERY_PARAM);
  currentUrl.searchParams.delete(GOOGLE_CONSENT_ERROR_QUERY_PARAM);

  const updatedUrl = `${currentUrl.pathname}${currentUrl.search}${currentUrl.hash}`;

  window.history.replaceState(window.history.state, '', updatedUrl);
}

export function useGoogleImportReturn(expectedSource: GoogleImportSource) {
  const [googleImportReturn] = useState(() =>
    getGoogleImportReturnState(expectedSource)
  );

  useEffect(() => {
    if (!googleImportReturn.shouldAutoOpen) {
      return;
    }

    if (googleImportReturn.consentError === GOOGLE_CONSENT_ACCOUNT_MISMATCH) {
      toast(GOOGLE_CONSENT_ACCOUNT_MISMATCH_MESSAGE);
    }

    clearGoogleImportReturnQueryParams();
  }, [googleImportReturn.consentError, googleImportReturn.shouldAutoOpen]);

  return {
    shouldAutoOpen: googleImportReturn.shouldAutoOpen,
  };
}

export { clearGoogleImportReturnQueryParams, getGoogleImportReturnState };
